import { Box, makeStyles } from '@material-ui/core'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { EnergyType } from '../../calculators/EnergyFlow'
import { PurchasedEnergyType } from '../../generated/graphql'
import { energyPriceUnit } from '../../util/formatters'
import { Section } from '../common/Section'
import type { PricingModelWithVersions } from '../costs/costs-report/CostsReport'
import {
  basicInfoFormatters,
  basicInfoKeys,
  formatPriceByEnergyType,
  formatPriceIncreaseRateByEnergyType,
} from './PricingModelAccordion'
import { PricingModelTable } from './PricingModelTable'

type Props = {
  pricingModel: PricingModelWithVersions
}

const useStyles = makeStyles({
  reportContainer: {
    maxWidth: '350px',
    display: 'inline-block',
    pageBreakInside: 'avoid',
  },
})

export function PricingModelReport({
  pricingModel,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const classes = useStyles()

  const getSubtitle = (): string => {
    const versionNames = pricingModel.usedInVersions.map(
      (version) => version.name,
    )
    if (versionNames.length > 0) {
      return `${t('pricingModels.usedInVersions')}: ${versionNames.toString()}`
    }
    return ''
  }

  return (
    <Box className={classes.reportContainer}>
      <Section title={pricingModel.name} subtitle={getSubtitle()}>
        <PricingModelTable
          header={t('pricingModels.basicInfo')}
          rowsData={basicInfoKeys.map((key) => ({
            name: t(`pricingModels.${key}`),
            value: basicInfoFormatters[key](pricingModel[key]),
          }))}
        />

        <PricingModelTable
          header={`${t('pricingModels.energyPrices')}, ${energyPriceUnit}`}
          rowsData={Object.values(PurchasedEnergyType).map((energyType) => ({
            name: t(`purchasedEnergyType.${energyType}`),
            value: formatPriceByEnergyType(energyType, 'prices', pricingModel),
          }))}
        />

        <PricingModelTable
          header={`${t(
            'pricingModels.energySellingPrices',
          )}, ${energyPriceUnit}`}
          rowsData={[PurchasedEnergyType.Electricity].map((energyType) => ({
            name: t(`energyType.${EnergyType.Electricity}`),
            value: formatPriceByEnergyType(
              energyType,
              'sellingPrices',
              pricingModel,
            ),
          }))}
        />

        <PricingModelTable
          header={`${t('pricingModels.energyPriceIncreaseRate')}, %`}
          rowsData={Object.values(PurchasedEnergyType).map((energyType) => ({
            name: t(`purchasedEnergyType.${energyType}`),
            value: formatPriceIncreaseRateByEnergyType(
              energyType,
              pricingModel,
            ),
          }))}
        />
      </Section>
    </Box>
  )
}
